
import { Priority } from "../../../_models";
import { TimePredictionRule } from "../classes/time-prediction-rule";
import { PATechnician } from "../classes/technician";
import { PAProject } from "../classes/project";
import { Subject } from "rxjs";
import { PAUtil, WeekDay } from "../classes/util";
import { PADataControl } from "./pa-data-control";
import { PlanningAssistantSettingsService } from "../services/planning-assistant-settings.service";

export type SettingTab = 'Allgemein' | 'Rampups (extra Zeit)' | 'Reisetechniker' | 'Projekte' | 'Planung' | 'Startzeiten'

export interface PASettingsConfig {
  id?: number
  title: string;
  data: PASettings
}

interface ShownWeekdays {
  monday: boolean;
  tuesday: boolean;
  wednesday: boolean;
  thursday: boolean;
  friday: boolean;
  saturday: boolean;
  sunday: boolean;
}

interface MapOptions {
  show_operation_times: boolean;
  seperated_tour_view: boolean;
}

interface ReloadOptions {
  projects: boolean,
  operations: boolean,
  technicians: boolean,
  days: boolean,
}

interface GeneralSettings {
  show_weekdays: ShownWeekdays;
  map: MapOptions;
  reload: ReloadOptions;
}

interface OperationPlanningOptions {
  show_close_technician_tours: boolean;
  show_close_technician_tours_distance: number;
}

interface TechnicianStartTime {
  user_id: number;
  start_minutes: number; // Minuten seit Mitternacht (z.B. 570 für 9:30)
  is_arrival_time?: boolean; // true = Ankunftszeit beim ersten Kunden, false/undefined = Losfahrzeit
}

interface WorkStartTimeSettings {
  default_start_minutes: number; // Minuten seit Mitternacht
  default_is_arrival_time?: boolean; // true = Ankunftszeit beim ersten Kunden, false/undefined = Losfahrzeit
  technician_start_times: TechnicianStartTime[];
}

interface HotelOvernightStayOptions {
  default_priority_id: number;
}

interface OptimizeTravelLocationsOptions {
  active: boolean;
  on_weekdays: WeekDay[];
}

interface TourPlanningSettings {
  operation_planning: OperationPlanningOptions;
  use_driving_time_factor: boolean;
  work_start_times: WorkStartTimeSettings;
}

interface TravelTechniciansSettings {
  hotel_overnight_stay: {
    default_priority_id: number
  };
  optimize_travel_locations: OptimizeTravelLocationsOptions;
}

export interface PASettings {
  general: GeneralSettings,
  tour_planning: TourPlanningSettings,
  travel_technicians: TravelTechniciansSettings
}

export class PASettingsControl {

  private static _instance: PASettingsControl
  public static get Instance()
  {
    return this._instance || (this._instance = new this());
  }

  additionalPriorityTimeRules = new Map<Priority, TimePredictionRule[]>()
  currentPriorityTimeRuleConfig: {technician?: PATechnician | 'default', project_priority: {project: PAProject, priority?: Priority}, extra_percent?: number} = {project_priority: {project: null, priority: null}}
  priorityRuleChangedSubject = new Subject<Priority>()

  showSettings = false
  settingTabs: SettingTab[] = ['Allgemein', 'Planung', 'Startzeiten', 'Reisetechniker', 'Rampups (extra Zeit)']
  activeSettingsTab: SettingTab = 'Allgemein'
  projectMode: 'service' | 'free' = 'free'

  loadedSettingsConfig: PASettingsConfig

  selectedSettingConfig: PASettingsConfig = {
    title: 'Default',
    data: {
      general: this.getDefaultGeneralSettings(),
      tour_planning: this.getDefaultTourPlanningSettings(),
      travel_technicians: this.getDefaultTravelTechnicianSettings()
    }
  }

  private getDefaultShownWeekDays(): ShownWeekdays {
    return {
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: true,
      sunday: false
    }
  }

  private _settingsService?: PlanningAssistantSettingsService

  // Schutz gegen doppeltes Auto-Create: loadSettingsFromDatabase wird von zwei
  // Einstiegspunkten aufgerufen (Dashboard + PA). Sehen beide eine (noch)
  // leere Liste, würde jeder ein eigenes Create auslösen — so sind über die
  // Zeit Dutzende "Default"-Duplikate in der Tabelle entstanden.
  private autoCreateTriggered = false

  private constructor(
  ) {
  }

  set settingsService(settings_service: PlanningAssistantSettingsService) {
    this._settingsService = settings_service
  }

  async loadSettingsFromDatabase(): Promise<void> {

    // null = Ladefehler (Server/Netzwerk) — bewusst unterscheidbar von der
    // leeren Liste „User hat keine Settings" (hawk#558, Aufgabe B): Ein
    // transienter 500 darf KEIN Auto-Create auslösen, sonst entsteht genau
    // das Duplikat, das der autoCreateTriggered-Schutz verhindern soll
    // (das Backend dedupliziert nicht).
    const settings = await new Promise<PASettingsConfig[] | null>(
      resolve => {
        if (this._settingsService) {
          this._settingsService.index().subscribe(
            settings => {
              resolve(settings)
            },
            error => {
              console.log(error)
              resolve(null)
            }
          )
        } else {
          resolve(null)
        }
      }
    )

    // Ladefehler: mit den In-Memory-Defaults weiterarbeiten, nichts
    // persistieren — der nächste erfolgreiche Load entscheidet neu.
    if (settings === null) return

    if (settings.length) {
      this.selectedSettingConfig = {
        id: settings[0].id,
        title: settings[0].title,
        data: {
          general: {
            show_weekdays: settings[0].data?.general?.show_weekdays || this.getDefaultShownWeekDays(),
            map: settings[0].data?.general?.map || this.getDefaultMapOptions(),
            reload: settings[0].data?.general.reload || this.getDefaultReloadOptions(),
          },
          travel_technicians: {
            optimize_travel_locations: settings[0].data?.travel_technicians?.optimize_travel_locations || this.getDefaultOptimizeTravelLocationOptions(),
            hotel_overnight_stay: settings[0].data?.travel_technicians?.hotel_overnight_stay || this.getDefaultHotelOvernightStayOptions()
          } ,
          tour_planning: {
            operation_planning: settings[0].data?.tour_planning?.operation_planning || this.getDefaultOperationPlanningOptions(),
            use_driving_time_factor: settings[0].data?.tour_planning?.use_driving_time_factor || false,
            work_start_times: settings[0].data?.tour_planning?.work_start_times || this.getDefaultWorkStartTimeSettings()
          }
        }
      }
      this.loadedSettingsConfig = JSON.parse(JSON.stringify(this.selectedSettingConfig))
    } else if (!this.autoCreateTriggered) {
      // Flag synchron VOR dem asynchronen Create setzen — ein parallel
      // laufender zweiter Load (Dashboard vs. PA) legt sonst ein Duplikat an.
      // Schlägt das Create fehl, wird das Flag wieder freigegeben, damit ein
      // späterer Lauf erneut anlegen darf (sonst bliebe der User die ganze
      // Session ohne persistierte Settings).
      this.autoCreateTriggered = true
      const created = await this.createOrUpdateSettingsInDatabase()
      if (!created) this.autoCreateTriggered = false
    }
  }

  // Liefert true bei erfolgreichem Speichern, false bei Fehler — genutzt vom
  // Auto-Create in loadSettingsFromDatabase, um das Trigger-Flag im
  // Fehlerfall wieder freizugeben.
  async createOrUpdateSettingsInDatabase(): Promise<boolean> {
    return new Promise(resolve => {
      if (this.selectedSettingConfig.id) {
        this._settingsService.update(this.selectedSettingConfig.id, {setting: {data: this.selectedSettingConfig.data, title: this.selectedSettingConfig.title}}).subscribe(
          setting => {
            this.selectedSettingConfig = setting
            this.loadedSettingsConfig = JSON.parse(JSON.stringify(this.selectedSettingConfig))
            resolve(true)
          },
          error => {
            console.log(error)
            resolve(false)
          }
        )
      } else {
        this._settingsService.create({setting: this.selectedSettingConfig}).subscribe(
          setting => {
            this.selectedSettingConfig = setting
            this.loadedSettingsConfig = JSON.parse(JSON.stringify(this.selectedSettingConfig))
            resolve(true)
          },
          error => {
            console.log(error)
            resolve(false)
          }
        )
      }
    })
  }

  settingsHaveChanged(): boolean {
    return JSON.stringify(this.selectedSettingConfig) != JSON.stringify(this.loadedSettingsConfig)
  }

  quickReloadSettingsHaveChanged(): boolean {
    return JSON.stringify(this.selectedSettingConfig.data.general.reload) != JSON.stringify(this.loadedSettingsConfig.data.general.reload)
  }

  addAdditionalTechnicianPriorityOperationTimeOnDay(technician: PATechnician, project_id: number, priority_id: number, extra_percent: number) {
    const project = PADataControl.Instance.getProject(project_id)
    const priority = PADataControl.Instance.getPriority(priority_id)
    this.currentPriorityTimeRuleConfig = {technician: technician, project_priority: {project: project, priority: priority}, extra_percent: extra_percent}
    this.showSettings = true
    this.activeSettingsTab = "Rampups (extra Zeit)"
  }

  getAdditionalPriorityTimeRules(priority: Priority) {
    if (!this.additionalPriorityTimeRules.has(priority)) {
      this.additionalPriorityTimeRules.set(priority, [])
    }
    return this.additionalPriorityTimeRules.get(priority)
  }

  addAdditionalTimePriorityRule(rule: TimePredictionRule): void {
    const rules = this.getAdditionalPriorityTimeRules(rule.priority)
    if (!rules.includes(rule)) rules.push(rule)
  }

  async removeAdditionalTimePriorityRule(rule: TimePredictionRule): Promise<void> {
    const rules = this.getAdditionalPriorityTimeRules(rule.priority)
    if (rules.includes(rule)) {
      await rule.deleteInDb().then(_ => { if (!rule.isDeletable()) {PAUtil.removeElementFromList(rules, rule)}})
    }
  }

  getAdditionalPriorityTimeRuleForTechnicianAndPriorityIndex(priority: Priority, technician: PATechnician, priority_index: number): TimePredictionRule {
    const rules = this.getAdditionalPriorityTimeRules(priority)

    const technician_specific_rules = rules.filter(rule => rule.technician == technician)
    const found_technician_specific_rule = technician_specific_rules.find(rule => rule.step_start <= priority_index && priority_index <= rule.step_end)

    if (technician_specific_rules.length) {
      return found_technician_specific_rule
    } else {
      return rules.find(rule => rule.technician == 'default' && rule.step_start <= priority_index && priority_index <= rule.step_end)
    }

  }

  // Gibt die Startzeit in Minuten seit Mitternacht für einen Techniker zurück (z.B. 570 für 9:30)
  getWorkStartMinutesForTechnician(technician_id: number): number {
    const work_start_times = this.selectedSettingConfig.data.tour_planning.work_start_times
    const technician_config = work_start_times.technician_start_times.find(t => t.user_id === technician_id)
    return technician_config ? technician_config.start_minutes : work_start_times.default_start_minutes
  }

  // Gibt die Startzeit in Millisekunden (Tageszeit) für einen Techniker zurück
  getWorkStartMsForTechnician(technician_id: number): number {
    return this.getWorkStartMinutesForTechnician(technician_id) * 60 * 1000
  }

  // Gibt zurück, ob die Startzeit als Ankunftszeit beim ersten Kunden gilt
  getIsArrivalTimeForTechnician(technician_id: number): boolean {
    const work_start_times = this.selectedSettingConfig.data.tour_planning.work_start_times
    const technician_config = work_start_times.technician_start_times.find(t => t.user_id === technician_id)
    return technician_config ? !!technician_config.is_arrival_time : !!work_start_times.default_is_arrival_time
  }

  // Gibt die Startzeit als "HH:mm" String zurück
  getWorkStartTimeStringForTechnician(technician_id: number): string {
    return this.minutesToTimeString(this.getWorkStartMinutesForTechnician(technician_id))
  }

  // Fügt eine individuelle Techniker-Startzeit hinzu oder aktualisiert sie
  setTechnicianStartMinutes(technician_id: number, start_minutes: number): void {
    const work_start_times = this.selectedSettingConfig.data.tour_planning.work_start_times
    const existing = work_start_times.technician_start_times.find(t => t.user_id === technician_id)
    if (existing) {
      existing.start_minutes = start_minutes
    } else {
      work_start_times.technician_start_times.push({ user_id: technician_id, start_minutes })
    }
  }

  // Entfernt eine individuelle Techniker-Startzeit (Techniker erhält dann die Standard-Zeit)
  removeTechnicianStartTime(technician_id: number): void {
    const work_start_times = this.selectedSettingConfig.data.tour_planning.work_start_times
    work_start_times.technician_start_times = work_start_times.technician_start_times.filter(t => t.user_id !== technician_id)
  }

  // Konvertiert Minuten seit Mitternacht zu "HH:mm" String
  minutesToTimeString(minutes: number): string {
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`
  }

  // Konvertiert "HH:mm" String zu Minuten seit Mitternacht
  timeStringToMinutes(timeString: string): number {
    const [hours, mins] = timeString.split(':').map(Number)
    return hours * 60 + (mins || 0)
  }

  private getDefaultMapOptions(): MapOptions {
    return {
      show_operation_times: true,
      seperated_tour_view: true
    }
  }

  private getDefaultGeneralSettings(): GeneralSettings {
    return {
      show_weekdays: this.getDefaultShownWeekDays(),
      map: this.getDefaultMapOptions(),
      reload: this.getDefaultReloadOptions()
    }
  }

  private getDefaultOperationPlanningOptions(): OperationPlanningOptions {
    return {
      show_close_technician_tours: true,
      show_close_technician_tours_distance: 5
    }
  }

  private getDefaultHotelOvernightStayOptions(): HotelOvernightStayOptions {
    return {
      default_priority_id: null
    }
  }

  private getDefaultOptimizeTravelLocationOptions(): OptimizeTravelLocationsOptions {
    return {
      active: true,
      on_weekdays: ["monday", "tuesday", "wednesday", "thursday"]
    }
  }

  private getDefaultTourPlanningSettings(): TourPlanningSettings {
    return {
      operation_planning: this.getDefaultOperationPlanningOptions(),
      use_driving_time_factor: false,
      work_start_times: this.getDefaultWorkStartTimeSettings()
    }
  }

  private getDefaultWorkStartTimeSettings(): WorkStartTimeSettings {
    return {
      default_start_minutes: 9 * 60, // 9:00 Uhr = 540 Minuten
      technician_start_times: []
    }
  }

  private getDefaultTravelTechnicianSettings(): TravelTechniciansSettings {
    return {
      hotel_overnight_stay: this.getDefaultHotelOvernightStayOptions(),
      optimize_travel_locations: this.getDefaultOptimizeTravelLocationOptions()
    }
  }

  private getDefaultReloadOptions(): ReloadOptions {
    return {
      days: true,
      operations: true,
      projects: true,
      technicians: true
    }
  }
}
